import {
  EmployeeCard,
  EmployeeCardSkeleton,
} from "@/components/company/employee-card";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToastError } from "@/components/ui/use-toast";
import { useAreSectionsPending } from "@/contexts/company-sections-context";
import { useUser } from "@/contexts/user-provider";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey } from "@/lib/company-refusal";
import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import { cn } from "@/lib/utils";
import { CompanyDTO, EmployeeDTO } from "@shaarp/common";
import { UsersRoundIcon } from "lucide-react";
import { useCallback, useEffect, useEffectEvent, useState } from "react";
import { NavLink } from "react-router-dom";

type CardCompanyEmployeesSuggestedProps = {
  company: CompanyDTO;
  className?: string;
  onSeeMoreClick?: () => void;
  variant?: "card" | "section";
};

/** The suggested key contacts; nothing, not even a fetch, for a team whose direct contact is cut (#757). */
export default function CardCompanyEmployeesSuggested(
  props: CardCompanyEmployeesSuggestedProps
) {
  const arePeopleVisible = useArePeopleVisible();
  if (!arePeopleVisible) return null;
  return <CardCompanyEmployeesSuggestedContent {...props} />;
}

function CardCompanyEmployeesSuggestedContent({
  company,
  className,
  onSeeMoreClick,
  variant = "card",
}: CardCompanyEmployeesSuggestedProps) {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();
  const { teamUser } = useUser();

  const [employeesSuggested, setEmployeesSuggested] = useState<EmployeeDTO[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Asked once these settled: asked earlier, the suggestion misses the people
  // they bring. Settled in the snapshot (a fresh page, the side panel), the
  // card asks at mount.
  const isWaitingForPeople = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.employeesSuggested,
  );

  // `api`, `toastError` and `t` are new on each render: read the latest ones.
  const fetchSuggested = useEffectEvent(async (signal: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get<EmployeeDTO[]>(
        `/company/${company.id}/employees/suggested?limit=3`,
        { signal }
      );
      if (signal.aborted) return;

      setEmployeesSuggested(data);
    } catch (err) {
      if (signal.aborted) return;
      // A refusal (the daily budget, a throttle) says why, not "error".
      const refusal = companyRefusalKey(err);
      toastError({
        title: t("common.error"),
        description: t(refusal ?? "common.errorFetchingData"),
      });
      setError(err instanceof Error ? err : new Error(String(err)));
      setEmployeesSuggested([]);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  });

  // The team's own contacts query shapes the suggestion: a new one asks again.
  const customSearchContactsQuery = teamUser?.customSearchContactsQuery;
  useEffect(() => {
    if (isWaitingForPeople) return;
    const controller = new AbortController();
    void fetchSuggested(controller.signal);
    return () => controller.abort();
  }, [customSearchContactsQuery, company.id, isWaitingForPeople]);

  const handleEmployeeChanged = useCallback(
    (update: Partial<EmployeeDTO> & { id: string }) => {
      setEmployeesSuggested((prev) =>
        prev.map((employee) =>
          employee.id === update.id ? { ...employee, ...update } : employee
        )
      );
    },
    []
  );

  if (error || (!isLoading && employeesSuggested.length === 0)) {
    return null;
  }

  const renderSeeMore = (sizeClass: string) => {
    const buttonClassName = cn(
      buttonVariants({ variant: "outline" }),
      "rounded-full shadow-sm active:brightness-90",
      sizeClass,
    );
    return onSeeMoreClick ? (
      <button type="button" onClick={onSeeMoreClick} className={buttonClassName}>
        {t("common.seeMore")}
      </button>
    ) : (
      <NavLink to={`/company/${company.id}/employees`} className={buttonClassName}>
        {t("common.seeMore")}
      </NavLink>
    );
  };

  if (variant === "section") {
    return (
      <section className={className}>
        <SectionHeaderWithIcon
          icon={UsersRoundIcon}
          title={t("company.employees.suggested")}
          rightSlot={renderSeeMore("h-8 px-3 text-xs")}
        />
        <div className="flex flex-col gap-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <EmployeeCardSkeleton key={index} />
              ))
            : employeesSuggested.map((employee) => (
                <EmployeeCard
                  key={employee.id}
                  employee={employee}
                  companyId={company.id}
                  onEmployeeChanged={handleEmployeeChanged}
                  compact
                />
              ))}
        </div>
      </section>
    );
  }

  return (
    <Card className={className}>
      <div className="flex flex-row justify-between">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-lg">
            <div className="shrink-0 rounded-full flex items-center justify-center w-10 h-10 bg-muted">
              <UsersRoundIcon className="w-5 h-5" />
            </div>
            <span>{t("company.employees.suggested")}</span>
          </CardTitle>
        </CardHeader>
        <div className="flex items-center p-5">
          {renderSeeMore("h-10 px-4")}
        </div>
      </div>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <EmployeeCardSkeleton key={index} />
              ))
            : employeesSuggested.map((employee) => (
                <EmployeeCard
                  key={employee.id}
                  employee={employee}
                  companyId={company.id}
                  onEmployeeChanged={handleEmployeeChanged}
                  compact
                />
              ))}
        </div>
      </CardContent>
    </Card>
  );
}

